/* eslint-disable max-lines, react/jsx-no-target-blank */
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';
import { plural } from '@cian/utils/lib/shared/plural';
import { mergeStyles } from '@cian/utils/lib/shared/style';

import {
  IBargainTermsSchema,
  IOfferSchema,
  IUserSubscriptionSchema,
  ECoworkingOfferType,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { IContext } from '../../store';
import { IncludedOption, VatType } from '../../store/offer';
import { FDealType, getDealType, isLongtermRent, isRent } from '../../utils/deal_type';
import { getLandAreaUnitType } from '../../utils/offer_helpers';
import { FOfferType, getOfferType, isCommercial } from '../../utils/offer_type';
import { getMoreRentPrice, getRentPrice } from '../../utils/price';
import { isSuburbanOfferFromBuilder } from '../../utils/suburban';
import { AgentFeedingContainer } from '../agent_feeding/container';
import { AsideTipCountrysideBuilderPromoContainer } from '../AsideTipCountrysideBuilderPromo';
import { OfferContactsAsideContainer } from '../contacts_aside/container';
import { MortgageCalculatorContainer } from '../mortgage-calculator/container';
import { PriceDropdownContainer } from '../price_dropdown/container';
import { PriceHistoryContainer } from '../price_history/container';
import { SubscriptionPriceChangesPopupContainer } from '../subscription_price_changes_popup/container';
import { IconMail } from './icons/icon_mail';
import { trackSubscribePopupOpen } from './tracking';
import { Tooltip, InfoIcon, TOOLTIP_CUSTOM_POSITION, TOOLTIP_CUSTOM_TYPE } from '@cian/ui-kit/prev';
import { ca } from '@cian/analytics';
import { CurrencySymbol, getPriceRentTimeSuffix, withNoBreakSpaces } from '../../utils/formatters';
import * as styles from './index.css';

const NBSP = '\u00a0';
const RUR = '\u20bd';

type TSaleType = Exclude<IBargainTermsSchema['saleType'], null>;

const bargainTermsSaleTypeMap: { [key in TSaleType]: string } = {
  alternative: 'альтернатива',
  dupt: 'переуступка',
  dzhsk: `договор${NBSP}ЖСК`,
  free: `свободная${NBSP}продажа`,
  fz214: `долевое${NBSP}участие${NBSP}(214-ФЗ)`,
  investment: `договор${NBSP}инвестирования`,
  pdkp: `предварительный${NBSP}договор`,
};

export interface IOfferTermsStateProps {
  abGroup?: number;
  realtyId?: number;
  offer: IOfferSchema;
  subscription: IUserSubscriptionSchema | null;
  isAgent: boolean;
  isAuthenticated: boolean | null;
  isMicrodataExperimentEnable: boolean;
  knAuctionCompanies: number[];
  excludedUtilitiesTermsRegions: number[];
  isDealRentCianAgencyEnabled: boolean;
}

export interface IOfferTermsDispatchProps {
  subscriptionPriceChangesPopupOpen(): void;
}

type IOfferTermsProps = IOfferTermsStateProps & IOfferTermsDispatchProps;

export interface IOfferTermsState {
  isPhonesOpened: boolean;
  isInfoVisible: boolean;
  infoHeight?: number | null;
}

export class OfferTerms extends React.Component<IOfferTermsProps, IOfferTermsState> {
  public context: IContext;
  private infoElement?: HTMLDivElement | null;
  private handleDocumentScroll: (() => void) | undefined;

  public static contextTypes = {
    config: PropTypes.object,
    logger: PropTypes.object,
    makeRequest: PropTypes.func,
    telemetry: PropTypes.object,
  };

  public static handleDocumentScrollHighOrder(callback: Function, timeoutMS: number = 100) {
    let scrollY = 0;
    let isActive = false;

    return () => {
      if (!isActive) {
        const isInfoVisible = (() => {
          if (window.scrollY <= 0) {
            return true;
          }

          return window.scrollY < scrollY;
        })();

        isActive = true;
        scrollY = window.scrollY;

        callback(isInfoVisible);

        window.setTimeout(() => (isActive = false), timeoutMS);
      }
    };
  }

  public constructor(props: IOfferTermsProps) {
    super(props);

    this.handleDocumentScroll = OfferTerms.handleDocumentScrollHighOrder(this.handleVisiblilityChange);

    this.state = {
      infoHeight: undefined,
      isInfoVisible: true,
      isPhonesOpened: false,
    };
  }

  public componentDidMount() {
    if (this.handleDocumentScroll) {
      document.addEventListener('scroll', this.handleDocumentScroll);
    }

    this.setState({
      infoHeight: this.infoElement && this.infoElement.offsetHeight,
    });
  }

  public componentWillUnmount() {
    if (this.handleDocumentScroll) {
      document.removeEventListener('scroll', this.handleDocumentScroll);
    }
  }

  public componentDidUpdate(prevProps: IOfferTermsProps, prevState: IOfferTermsState) {
    if (
      prevState.isPhonesOpened !== this.state.isPhonesOpened ||
      prevProps.subscription !== this.props.subscription ||
      prevProps.offer !== this.props.offer
    ) {
      this.setState({
        infoHeight: this.infoElement && this.infoElement.offsetHeight,
      });
    }
  }

  private trackShowTooltip = () => {
    const { abGroup, realtyId, offer } = this.props;

    ca('eventSite', {
      name: 'oldevent',
      ec: 'Card',
      ea: 'trade_popup_show',
      el: '',
      page: {
        pageType: 'Card',
        siteType: 'desktop',
      },
      user: {
        userId: realtyId,
        abGroup,
      },
      products: {
        id: offer.id,
        offerType: 'offer',
        position: 1,
      },
    });
  };

  private trackTooltipLinkHandler = () => {
    const { abGroup, realtyId, offer } = this.props;

    ca('eventSite', {
      name: 'oldevent',
      ec: 'Card',
      ea: 'trade_popup_click',
      el: '',
      page: {
        pageType: 'Card',
        siteType: 'desktop',
      },
      user: {
        userId: realtyId,
        abGroup,
      },
      products: {
        id: offer.id,
        offerType: 'offer',
        position: 1,
      },
    });
  };

  public render() {
    const { offer, knAuctionCompanies, subscription } = this.props;
    const { status, coworkingOfferType } = offer;
    const offerType = getOfferType(offer);
    const isCom = isCommercial(offerType);
    const isCoworking = !!offer.coworkingOfferType;
    const showCommercialPrice = offer.publishedUserId && !knAuctionCompanies.includes(offer.publishedUserId);
    const description = !isCom ? this.getDescription() : this.getCommercialDescription();
    const isFlexOffice = coworkingOfferType === ECoworkingOfferType.Office;

    let infoContainerHeight;

    if (this.infoElement && this.state.infoHeight) {
      infoContainerHeight = this.state.isInfoVisible ? `${this.state.infoHeight}px` : '0';
    }

    return (
      <div
        {...mergeStyles([
          styles['container'],
          isCom && styles['container--commercial'],
          infoContainerHeight === '0' && styles['container--hidden'],
        ])}
      >
        <div className={styles['terms']}>
          <div className={styles['price-container']}>{this.renderPrice()}</div>
          <div
            style={{
              height: isCom && infoContainerHeight ? infoContainerHeight : 'auto',
              overflow: isCom && !isCoworking ? 'hidden' : 'initial',
            }}
            className={styles['info-container']}
          >
            <div
              ref={item => {
                this.infoElement = item;
              }}
              className={styles['info']}
            >
              {showCommercialPrice && subscription && status === 'published' && !isFlexOffice && (
                <div className={styles['price_changes']} onClick={this.handleSubscriptionClick}>
                  <span>
                    <IconMail />
                    {subscription.isSubscribed ? 'Отменить подписку на цену' : 'Следить за изменением цены'}
                  </span>
                </div>
              )}
              {showCommercialPrice && description && <p className={styles['description']}>{description}</p>}
              <AgentFeedingContainer />
              <SubscriptionPriceChangesPopupContainer />
              {status === 'published' && !isCom && <OfferContactsAsideContainer />}
            </div>
          </div>
          <AsideTipCountrysideBuilderPromoContainer />
        </div>
      </div>
    );
  }

  private handleSubscriptionClick = () => {
    this.props.subscriptionPriceChangesPopupOpen();
    trackSubscribePopupOpen();
  };

  private handleVisiblilityChange = (isInfoVisible: boolean) => {
    this.setState({ isInfoVisible });
  };

  private renderResidentialPrice() {
    const { offer, isMicrodataExperimentEnable, excludedUtilitiesTermsRegions } = this.props;
    const { bargainTerms } = offer;
    const dealType = getDealType(offer);
    const offerType = getOfferType(offer);
    const postfix = getPriceRentTimeSuffix(offer);
    const price = isRent(dealType)
      ? `${getRentPrice(offer, excludedUtilitiesTermsRegions, CurrencySymbol.Rur)}${postfix}`
      : `${numberToPrettyString(Math.round(bargainTerms.prices.rur))} ${CurrencySymbol.Rur}`;

    const microData = isMicrodataExperimentEnable
      ? {
          itemProp: 'offers',
          itemScope: true,
          itemType: 'http://schema.org/Offer',
        }
      : {};

    return (
      <div className={`${styles['price']} ${styles['price--residential']}`}>
        <span {...microData} className={`${styles['price_value']}`}>
          <span {...{ itemProp: 'price', content: price }}>{withNoBreakSpaces(price)}</span>
          <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
        </span>
        <PriceHistoryContainer />
        {this.renderPricePerMeter()}
        <PriceDropdownContainer className={styles['price_in_currency']} />
        {/**
         * Ипотечный калькулятор не доступен для продажи участка и загородного застройщика,
         * так как поле с ипотекой нельзя добавить на форме подачи для участков.
         */
        dealType === FDealType.Sale && offerType !== FOfferType.Land && !isSuburbanOfferFromBuilder(offer) && (
          <div className={styles['morgage-container']}>
            <MortgageCalculatorContainer />
          </div>
        )}
        {/**
         * Для коммерческой не показываем сноску - Цена аренды + коммунальные
         */
        isLongtermRent(dealType) && <div className={styles['more_price_rent']}>{getMoreRentPrice(offer)}</div>}
      </div>
    );
  }

  private renderCommercialAuctuionNote() {
    return (
      <div>
        <span className={styles['price-container-auction']}>Электронные торги</span>
        <Tooltip
          master={
            <span className={styles['price-container-auction-icon']}>
              <InfoIcon custom={{ size: '20' }} />
            </span>
          }
          styles={{
            display: 'inline-block',
            backgroundColor: 'FFFFFF',
            borderColor: '121212',
            boxShadow: '',
            zIndex: '4',
          }}
          custom={{
            position: TOOLTIP_CUSTOM_POSITION['top'],
            backgroundOpacity: '1',
            type: TOOLTIP_CUSTOM_TYPE['hover'],
            alignedOnEdge: false,
            mediaType: 'desktop',
          }}
          onOpen={this.trackShowTooltip}
        >
          Объект реализуется через проведение торгов в соответствии с ГК РФ.&nbsp;
          <a
            href="https://cian.zendesk.com/hc/ru/articles/360037867052"
            target="_blank"
            onClick={this.trackTooltipLinkHandler}
          >
            Справочная информация
          </a>
        </Tooltip>
      </div>
    );
  }

  private renderCommercialPrice() {
    const { offer, isMicrodataExperimentEnable, knAuctionCompanies } = this.props;
    const dealType = getDealType(offer);
    const showPrice = offer.cianUserId && !knAuctionCompanies.includes(offer.cianUserId);

    if (!showPrice) {
      return this.renderCommercialAuctuionNote();
    }

    const microData = isMicrodataExperimentEnable
      ? {
          itemProp: 'offers',
          itemScope: true,
          itemType: 'http://schema.org/Offer',
        }
      : {};

    if (dealType === FDealType.Sale) {
      if (offer.priceTotalRur !== null) {
        if (offer.minArea !== null && offer.minPriceTotalRur !== null) {
          return (
            <div
              {...Object.assign({}, microData, {
                itemType: 'http://schema.org/AggregateOffer',
              })}
              className={styles['price']}
            >
              <div>
                <span className={styles['price-label']}>от</span>&nbsp;
                <span {...{ itemProp: 'lowPrice' }}>
                  {withNoBreakSpaces(numberToPrettyString(Math.round(offer.minPriceTotalRur)))}
                </span>
                <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
              </div>
              <div>
                <span className={styles['price-label']}>до</span>&nbsp;
                <span {...{ itemProp: 'highPrice' }}>
                  {withNoBreakSpaces(numberToPrettyString(Math.round(offer.priceTotalRur)))}&nbsp;&#8381;&nbsp;
                </span>
                <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
                {!this.showPriceHistoryForCommercial() && <PriceHistoryContainer />}
                <PriceDropdownContainer className={styles['price_in_currency']} />
              </div>
              <div>{this.renderPricePerMeter()}</div>
            </div>
          );
        } else {
          return (
            <div {...microData} className={styles['price']}>
              <span {...{ itemProp: 'price', content: offer.priceTotalRur }}>
                {withNoBreakSpaces(numberToPrettyString(Math.round(offer.priceTotalRur)))}&nbsp;&#8381;&nbsp;
              </span>
              <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
              {!this.showPriceHistoryForCommercial() && <PriceHistoryContainer />}
              <PriceDropdownContainer className={styles['price_in_currency']} />
              <div>{this.renderPricePerMeter()}</div>
            </div>
          );
        }
      }
    }

    if (offer.priceTotalPerMonthRur !== null) {
      let postfix: string | undefined;
      // tslint:disable-next-line:switch-default
      switch (dealType) {
        case FDealType.RentLongterm:
          postfix = '/мес.';
          break;
        case FDealType.RentDaily:
          postfix = '/сут.';
          break;
      }

      if (offer.minArea !== null && offer.minPriceTotalPerMonthRur !== null) {
        return (
          <div
            {...Object.assign({}, microData, {
              itemType: 'http://schema.org/AggregateOffer',
            })}
            className={styles['price']}
          >
            <div>
              <span className={styles['price-label']}>от</span>&nbsp;
              <span {...{ itemProp: 'lowPrice' }}>
                {numberToPrettyString(Math.round(offer.minPriceTotalPerMonthRur)).replace(' ', '\u00a0')}
              </span>
            </div>
            <div>
              <span className={styles['price-label']}>до</span>&nbsp;
              <span {...{ itemProp: 'highPrice' }}>
                {numberToPrettyString(Math.round(offer.priceTotalPerMonthRur)).replace(' ', '\u00a0')}&nbsp;&#8381;
              </span>
              <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
              <span className={styles['price-label']}>{postfix}</span>&nbsp;
              {!this.showPriceHistoryForCommercial() && <PriceHistoryContainer />}
              <PriceDropdownContainer className={styles['price_in_currency']} />
            </div>
            <div>{this.renderPricePerMeter()}</div>
          </div>
        );
      } else {
        const {
          coworkingOfferType,
          priceTotalPerMonthRur,
          bargainTerms: { priceForWorkplace },
        } = offer;

        const isFlexOffice = coworkingOfferType === ECoworkingOfferType.Office;
        const priceValue = isFlexOffice && priceForWorkplace ? priceForWorkplace : priceTotalPerMonthRur;

        // tslint:disable-next-line:max-line-length
        let price = `${numberToPrettyString(Math.round(priceValue)).replace(' ', '\u00a0')}\u00a0\u20bd`;

        postfix += isFlexOffice ? ' за рабочее место' : '';

        const renderSubPrice = isFlexOffice
          ? this.renderCoworkingPriceForAll(priceTotalPerMonthRur)
          : this.renderPricePerMeter();

        return (
          <div {...mergeStyles(styles['price'], isFlexOffice && styles['coworking'])}>
            <span {...microData}>
              <span {...{ itemProp: 'price', content: price }}>{price}</span>
              <span {...{ itemProp: 'priceCurrency', content: 'RUB' }} />
              <span className={styles['price-label']}>{postfix}</span>&nbsp;
            </span>
            {!isFlexOffice && (
              <>
                {!this.showPriceHistoryForCommercial() && <PriceHistoryContainer />}
                <PriceDropdownContainer className={styles['price_in_currency']} />
              </>
            )}
            <div>{renderSubPrice}</div>
          </div>
        );
      }
    }

    return null;
  }

  private renderCoworkingPriceForAll(priceTotalPerMonthRur: number) {
    const price = `${numberToPrettyString(Math.round(priceTotalPerMonthRur)).replace(
      ' ',
      '\u00a0',
    )}\u00a0\u20bd/мес. за весь офис`;

    return (
      <div className={styles['price_per_meter']}>
        {price}
        {this.showPriceHistoryForCommercial() && (
          <span className={styles['price_history_icon']}>
            <PriceHistoryContainer />
          </span>
        )}
      </div>
    );
  }

  private renderPrice() {
    const offerType = getOfferType(this.props.offer);

    if (isCommercial(offerType)) {
      return this.renderCommercialPrice();
    }

    return this.renderResidentialPrice();
  }

  private renderPricePerMeter() {
    const { offer } = this.props;
    const { areaParts } = offer;
    const dealType = getDealType(offer);
    const offerType = getOfferType(offer);

    if (offerType & FOfferType.Residential) {
      if (dealType === FDealType.Sale) {
        const { bargainTerms } = offer;

        let price: string | undefined;

        if (offerType === FOfferType.Land) {
          if (offer.land && offer.land.area) {
            price = `${numberToPrettyString(
              Math.round(bargainTerms.prices.rur / Number(offer.land.area)),
            )} \u20bd/${getLandAreaUnitType(offer.land)}`;
          }
          /**
           * Для комнаты цену за квадратный метр считаем от площади этой комнаты.
           */
        } else if (offerType === FOfferType.Room) {
          if ((offer.allRoomsArea && offer.roomsForSaleCount && offer.roomsForSaleCount > 0) || offer.roomArea) {
            /**
             *  Из апи могут приходить 3 формата строки:
             *  плюс используется для обозначения смежных комнат
             *  минус используется для обозначения изолированных комнат
             *  через пробел
             *
             *  числа разделяются запятыми
             */
            const rooms =
              offer.allRoomsArea &&
              offer.allRoomsArea
                .replace(/,/g, '.')
                .replace(/\+/g, '-')
                .replace(/\s/g, '-')
                .split('-');

            if (rooms && rooms.length > 1) {
              const roomsSquare = rooms.reduce((acc, current) => acc + Number(current), 0);
              if (!isNaN(roomsSquare)) {
                price = `${numberToPrettyString(Math.round(bargainTerms.prices.rur / roomsSquare))} \u20bd/м\u00b2`;
              }
            } else if (rooms && rooms.length > 0 && Number(rooms[0]) !== 0) {
              price = `${numberToPrettyString(Math.round(bargainTerms.prices.rur / Number(rooms[0])))} \u20bd/м\u00b2`;
            } else if (offer.roomArea) {
              price = `${numberToPrettyString(
                Math.round(bargainTerms.prices.rur / Number(offer.roomArea)),
              )} \u20bd/м\u00b2`;
            }
          }
        } else if (offer.totalArea) {
          price = `${numberToPrettyString(
            Math.round(bargainTerms.prices.rur / Number(offer.totalArea)),
          )} \u20bd/м\u00b2`;
        }

        if (!price) {
          return null;
        }

        return <div className={`${styles['price_per_meter']} ${styles['price_per_meter--residential']}`}>{price}</div>;
      }
    } else if (isCommercial(offerType)) {
      let price: string | undefined;

      if (areaParts && areaParts.length > 0) {
        let preparedByPrice;
        if (dealType & FDealType.Rent) {
          preparedByPrice = areaParts.map(i => i.price && i.area && (i.price * 12) / Number(i.area));
        } else {
          preparedByPrice = areaParts.map(i => i.price && i.area && i.price / Number(i.area));
        }

        const minAreaPrice = Math.min.apply(null, preparedByPrice);
        const paymentPeriodTitle = dealType & FDealType.Rent ? 'в год' : '';
        price = `от ${numberToPrettyString(Math.round(minAreaPrice))} \u20bd за м\u00b2 ${paymentPeriodTitle}`;
      } else if (dealType & FDealType.Rent) {
        if (offer.pricePerUnitAreaPerYearRur) {
          if (offerType === FOfferType.CommercialLand) {
            price = `${numberToPrettyString(
              Math.round(offer.pricePerUnitAreaPerYearRur),
            )} \u20bd за ${getLandAreaUnitType(offer.land)} в год`;
          } else {
            price = `${numberToPrettyString(Math.round(offer.pricePerUnitAreaPerYearRur))} \u20bd за м\u00b2 в год`;
          }
        }
      } else if (dealType === FDealType.Sale) {
        const { bargainTerms } = offer;
        if (offer.pricePerUnitAreaRur && offerType === FOfferType.CommercialLand) {
          price = `${numberToPrettyString(Math.round(offer.pricePerUnitAreaRur))} \u20bd за ${getLandAreaUnitType(
            offer.land,
          )}`;
        } else if (bargainTerms.priceType !== 'all') {
          const cost = numberToPrettyString(bargainTerms.prices.rur);
          const unit = bargainTerms.priceType === 'squareMeter' ? '\u20bd за м\u00b2' : '\u20bd';

          price = `${cost} ${unit}`;
        } else if (offer.pricePerUnitAreaRur) {
          price = `${numberToPrettyString(offer.pricePerUnitAreaRur)} \u20bd за м\u00b2`;
        } else if (offer.totalArea) {
          const totalArea = parseFloat(offer.totalArea);

          if (totalArea > 0) {
            const pricePerMeter = Math.round(offer.bargainTerms.prices.rur / totalArea);

            price = `${numberToPrettyString(pricePerMeter)} \u20bd за м\u00b2`;
          }
        }
      }

      if (price) {
        return (
          <div className={`${styles['price_per_meter']} ${styles['price_per_meter--commercial']}`}>
            {price}
            {this.showPriceHistoryForCommercial() && (
              <span className={styles['price_history_icon']}>
                <PriceHistoryContainer />
              </span>
            )}
          </div>
        );
      }
    }

    return null;
  }

  private showPriceHistoryForCommercial() {
    // Выводим стрелку истории цены для коммерческой ставки, если
    // - объявка коммерческая
    // - цена не является целым числом
    // - объявка не коммерческая земля
    // - указана площадь и цена за нее (ставка)
    const { offer } = this.props;
    const offerType = getOfferType(offer);

    return (
      isCommercial(offerType) &&
      offerType !== FOfferType.CommercialLand &&
      offer.minArea !== null &&
      (offer.minPriceTotalPerMonthRur !== null || offer.minPriceTotalRur !== null)
    );
  }

  private getDescription() {
    const { offer } = this.props;
    const { bargainTerms } = offer;

    const dealType = getDealType(offer);

    let descriptionArr: (string | null)[] = [];
    // tslint:disable-next-line:switch-default
    switch (dealType) {
      case FDealType.RentLongterm:
        descriptionArr.push(
          ...[
            (bargainTerms.depositPrices &&
              bargainTerms.depositPrices.rur &&
              `залог${NBSP}${numberToPrettyString(bargainTerms.depositPrices.rur).replace(' ', NBSP)}${NBSP}${RUR}`) ||
              `без${NBSP}залога`,
            this.getFormatedFee(),
            (bargainTerms.prepayMonths &&
              `предоплата${NBSP}за${NBSP}${bargainTerms.prepayMonths}${NBSP}${plural(bargainTerms.prepayMonths, [
                'месяц',
                'месяца',
                'месяцев',
              ])}`) ||
              `без${NBSP}предоплаты`,
            `${
              bargainTerms.leaseTermType && bargainTerms.leaseTermType === 'fewMonths'
                ? `на${NBSP}несколько${NBSP}месяцев`
                : `от${NBSP}года`
            }`,
            (bargainTerms.bargainAllowed && `возможен${NBSP}торг`) || null,
            (bargainTerms.tenantsType !== 'any' &&
              ((bargainTerms.tenantsType === 'family' && `только${NBSP}для${NBSP}семьи`) ||
                (bargainTerms.tenantsType === 'female' && `только${NBSP}для${NBSP}женщины`) ||
                (bargainTerms.tenantsType === 'male' && `только${NBSP}для${NBSP}мужчины`))) ||
              null,
          ],
        );
        break;
      case FDealType.RentDaily:
        descriptionArr.push(
          ...[
            `посуточная${NBSP}аренда`,
            (bargainTerms.depositPrices &&
              bargainTerms.depositPrices.rur &&
              `залог${NBSP}${numberToPrettyString(bargainTerms.depositPrices.rur).replace(' ', NBSP)}${NBSP}${RUR}`) ||
              `без${NBSP}залога`,
            (bargainTerms.bargainAllowed && `возможен${NBSP}торг`) || null,
            (bargainTerms.tenantsType !== 'any' &&
              ((bargainTerms.tenantsType === 'family' && `только${NBSP}для${NBSP}семьи`) ||
                (bargainTerms.tenantsType === 'female' && `только${NBSP}для${NBSP}женщины`) ||
                (bargainTerms.tenantsType === 'male' && `только${NBSP}для${NBSP}мужчины`))) ||
              null,
          ],
        );
        break;
      case FDealType.Sale:
        descriptionArr.push(
          ...[
            (bargainTerms.saleType && bargainTermsSaleTypeMap[bargainTerms.saleType]) || null,
            (bargainTerms.mortgageAllowed && `возможна${NBSP}ипотека`) || null,
          ],
        );
        break;
    }

    descriptionArr = descriptionArr.filter(Boolean);

    if (descriptionArr.length > 0) {
      const description = descriptionArr.join(', ');

      return description.charAt(0).toUpperCase() + description.slice(1);
    }

    return null;
  }

  private getClientFee(): number {
    return (this.props.offer && this.props.offer.bargainTerms && this.props.offer.bargainTerms.clientFee) || 0;
  }

  private getAgentFee(): number {
    return (this.props.offer && this.props.offer.bargainTerms && this.props.offer.bargainTerms.agentFee) || 0;
  }

  private getFormatedFee(): string {
    const agentFee = this.getAgentFee();
    const clientFee = this.getClientFee();
    const { isAgent, isDealRentCianAgencyEnabled } = this.props;

    if (isDealRentCianAgencyEnabled && clientFee !== 0) {
      return `комиссия${NBSP}${clientFee}%${NBSP}в${NBSP}пользу${NBSP}Циан`;
    }

    const result = [
      clientFee !== 0 ? `комиссия${NBSP}${clientFee}%` : null,
      agentFee !== 0 && isAgent ? `для${NBSP}агента${NBSP}${agentFee}%` : null,
    ]
      .filter(Boolean)
      .join(', ');

    return result || `без${NBSP}комиссии`;
  }

  private getCommercialDescription() {
    const { offer } = this.props;
    const {
      bargainTerms: { vatType, includedOptions },
    } = offer;

    const translatedIncludedOptions = (includedOptions || [])
      .map(
        option =>
          (option === IncludedOption.UtilityCharges && 'коммунальные\u00a0платежи') ||
          (option === IncludedOption.OperationalCosts && 'эксплуатационные\u00a0расходы') ||
          null,
      )
      .filter(Boolean);

    // пока для коворкингов оставляем только коммуналку и эксплуатационные расходы
    if (offer.coworkingOfferType) {
      return translatedIncludedOptions.length > 0 ? `Включены ${translatedIncludedOptions.join(', ')}` : '';
    }

    const formattedOptions: (string | null)[] = [
      ...translatedIncludedOptions,
      (vatType === VatType.Included && 'НДС') || null,
      (vatType === VatType.Usn && 'УСН') || null,
    ].filter(Boolean);

    // Если опции не включены и тип налога УСН то слово Включены не пишем (только в этом случае)
    const prefix = vatType === VatType.Usn && translatedIncludedOptions.length === 0 ? '' : 'Включены ';

    // Теперь надо совместить слово "Включены" опции, тип налога, и дополнение если налог не включен
    const optionsWithVat = formattedOptions.length > 0 ? `${prefix}${formattedOptions.join(', ')}` : '';

    const commercialFee = this.formatCommercialFee();
    if (optionsWithVat.length > 0 && commercialFee.length > 0) {
      return `${optionsWithVat}; ${commercialFee}`;
    } else if (optionsWithVat.length > 0) {
      return `${optionsWithVat}`;
    }

    return `${commercialFee.slice(0, 1).toUpperCase()}${commercialFee.slice(1)}`;
  }

  private formatCommercialFee(): string {
    const { isAuthenticated, isAgent, offer } = this.props;
    const { clientFee, agentFee } = offer.bargainTerms;

    return [
      clientFee !== null && clientFee !== 0 ? `комиссия\u00a0${clientFee}%` : null,
      clientFee !== null && clientFee === 0 ? `без\u00a0комиссии` : null,
      agentFee !== null && isAuthenticated && isAgent ? `для\u00a0агента\u00a0${agentFee}%` : null,
    ]
      .filter(Boolean)
      .join(', ');
  }
}
